import React, { useState, useCallback, useRef, useEffect } from 'react'
import { toast } from 'react-toastify'
import TextFieldNew, {
  TextFieldProps
} from 'presentation/shared/components/TextFieldNew'
import { debounce } from 'lodash'
import LoadingGif from 'presentation/assets/icons/loading.gif'

import * as S from './styles'
import { ReactComponent as CloseIcon } from 'presentation/assets/icons/close.svg'
import ButtonNew from '../ButtonNew'
import DividerNew from '../DividerNew'

export type AutoCompleteProps = {
  fixedSuggestions?: { label: string; value: any }[]
  suggestions?: { label: string; value: any }[]
  onSuggestionClick?: (value: any) => void
  onType?: (value: any) => void
  onIconClick?: (value: any) => void
  hasToggleShowSuggestions?: boolean
  onInputChange: (value: any) => void
  clear?: () => void
  debounceDelay?: number
  minCharactersSearch?: number
} & TextFieldProps

const AutoCompleteNew = ({
  fixedSuggestions,
  suggestions,
  onSuggestionClick,
  onType,
  onIconClick,
  hasToggleShowSuggestions = false,
  onInputChange,
  clear,
  debounceDelay = 500,
  minCharactersSearch = 0,
  ...inputProps
}: AutoCompleteProps) => {
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [loading, setLoading] = useState(true)

  const wrapperRef = useRef(null) as any
  const [suggestionContainerVisible, setSuggestionContainerVisible] =
    useState(false)

  function handleClickOutside(event: any) {
    if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
      setSuggestionContainerVisible(false)
      setShowSuggestions(false)
    }
  }

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [wrapperRef])

  const changeTrigger = async (userInput: string) => {
    if (userInput.length >= minCharactersSearch) {
      try {
        setLoading(true)
        setShowSuggestions(true)
        setSuggestionContainerVisible(true)
        onType && (await onType(userInput))
      } catch (err: any) {
        toast.error(err.message)
      } finally {
        setLoading(false)
      }
    }
  }

  const debounced = useCallback(
    debounce((func, e) => func(e), debounceDelay),
    []
  )

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const userInput = e.target.value
    setShowSuggestions(false)
    onInputChange(userInput)
    debounced(changeTrigger, userInput)

    if (!userInput) {
      setShowSuggestions(false)
    }
  }

  const onItemClick = (event: React.MouseEvent<HTMLLIElement>, value: any) => {
    setShowSuggestions(false)
    try {
      onSuggestionClick?.(value)
    } catch {
      return
    }
  }

  const onToggleShowSuggestionsClick = () =>
    // e: React.ChangeEvent<HTMLInputElement>
    {
      if (
        hasToggleShowSuggestions &&
        (fixedSuggestions?.length || suggestions?.length)
      ) {
        setLoading(false)
        setShowSuggestions(true)
        setSuggestionContainerVisible(true)
      }
    }

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // User pressed the enter key
    if (e.keyCode === 13) {
      setShowSuggestions(false)
    }
  }

  const SuggestionsListComponent = () => {
    if (loading) {
      return (
        <S.Suggestions className="suggestions">
          <li style={{ textAlign: 'center' }}>
            <img src={LoadingGif} alt="loading" />
          </li>
        </S.Suggestions>
      )
    }
    return (
      <S.Suggestions className="suggestions">
        {(!suggestions || suggestions?.length === 0) && // eslint-disable-line react/prop-types
          (!fixedSuggestions || fixedSuggestions?.length === 0) && // eslint-disable-line react/prop-types
          !loading &&
          suggestionContainerVisible && (
            <li ref={wrapperRef} style={{ textAlign: 'center' }}>
              Nenhum registro encontrado
            </li>
          )}
        {suggestionContainerVisible && (
          <div ref={wrapperRef}>
            {/* eslint-disable-next-line react/prop-types */}
            {fixedSuggestions?.map((suggestion, index) => {
              return (
                <li
                  key={`fixed-suggestion-${index}`}
                  onClick={(e) => onItemClick(e, suggestion.value)}
                >
                  {suggestion.label}
                </li>
              )
            })}

            {/* eslint-disable-next-line react/prop-types */}
            {fixedSuggestions?.length && suggestions?.length ? (
              <DividerNew marginTop="4px" marginBottom="4px" />
            ) : null}

            {/* eslint-disable-next-line react/prop-types */}
            {suggestions?.map((suggestion, index) => {
              return (
                <li
                  key={index}
                  data-testid={`doctor-autocomplete-doctor-${index}`}
                  onClick={(e) => onItemClick(e, suggestion.value)}
                >
                  {suggestion.label}
                </li>
              )
            })}
          </div>
        )}
      </S.Suggestions>
    )
  }

  return (
    <S.Wrapper isOpen={showSuggestions}>
      <TextFieldNew
        className="input"
        type="text"
        onChange={onChange}
        onKeyDown={onKeyDown}
        autoComplete="off"
        onIconClick={onToggleShowSuggestionsClick || onIconClick}
        {...inputProps}
        data-testid="autocomplete-tuss-field"
      />
      {showSuggestions && <SuggestionsListComponent />}
      {clear && inputProps.disabled && (
        <ButtonNew onClick={clear} className="clear-btn" type="button">
          <CloseIcon className="icon" />
        </ButtonNew>
      )}
    </S.Wrapper>
  )
}

export default AutoCompleteNew
